import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
	Button,
	Dropdown,
	Form,
	Grid,
	Header,
	Loader,
	Message,
	MessageHeader,
	Segment,
	Table,
} from "semantic-ui-react";

import Tooltip from "rc-tooltip";
import {
	createDataLayerFieldAssociation,
	dataLayerFieldAssociationsQueryOptions,
	dataLayerQueryOptions,
	deleteDataLayerFieldAssociation,
	fetchFromAzureFileShare,
	searchFieldsByVectorDataLayerQueryOptions,
	vectorDataLayerUploadsQueryOptions,
} from "../../../../apis/dataLayerApi";
import type {
	DataLayerTypeSelectOption,
	FieldWithBoundaryAndPlots,
	UUID,
	VectorDataLayerUploadStdResult,
} from "../../../../apis/types/dataLayerTypes";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import DataLayerMap from "./DataLayerMap";

import FieldActions from "./FieldActions";
import styles from "./styles.module.css";

type SortColumn = "field" | "growingSeason";
type SortDirection = "ascending" | "descending";

/**
 * Component which manages associations between Fields and the user's selected DataLayer.
 */
interface VectorDataLayerFieldAssocProps {
	fieldAssocComplete: boolean;
	onFieldAssocComplete: (complete: boolean) => void;
}

const VectorDataLayerFieldAssoc = ({ fieldAssocComplete, onFieldAssocComplete }: VectorDataLayerFieldAssocProps) => {
	const moduleNavigation = useModuleNavigation() as {
		flightId: string;
		createFlightLink: (useIdsFromUrl: boolean, flightId: string | null, submodule: string | null) => string;
		createFieldLink: (useIdsFromUrl: boolean, fieldId: string) => string;
	};
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };
	const history = useHistory();
	const queryClient = useQueryClient();

	/*
	 * State
	 */

	const dataLayerId = moduleNavigation.flightId; // workaround for hijacking the Flight Wizard
	const [failedToLoad, setFailedToLoad] = useState(false);

	// state of data layer <> fields associations
	const [associatedFields, setAssociatedFields] = useState<Set<UUID>>(new Set());
	const [existingAssociatedFields, setExistingAssociatedFields] = useState<Set<UUID>>(new Set());
	const [isSavingAssociations, setIsSavingAssociations] = useState(false);

	const [sortConfig, setSortConfig] = useState<{ column: SortColumn; direction: SortDirection }>({
		column: "growingSeason",
		direction: "descending",
	});

	// boolean toggles for field id -> boundaries, plots enabled on the map
	const [plotsVisibleOnMap, setPlotsVisibleOnMap] = useState<Record<UUID, boolean>>({});

	// currently selected data layer upload / zonation on the map.
	const [selectedDataLayerDisplayId, setSelectedDataLayerDisplayId] = useState<UUID | undefined>(undefined);

	/*
	 * Queries and Mutations
	 */

	// fetch DataLayerOptions query and queryOptions
	const {
		data: dataLayerOptions,
		error: dataLayerOptionsError,
		isFetching: dataLayerOptionsIsFetching,
	} = useQuery(dataLayerQueryOptions(getTokenSilently, userAuth.currentClientId));

	// SearchFieldsByDataLayer query and queryOptions
	const {
		data: fieldsWithBoundaries,
		error: searchFieldsByDataLayerError,
		isFetching: searchFieldsByDataLayerIsFetching,
	} = useQuery(searchFieldsByVectorDataLayerQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	// GetDataLayerUploads query and queryOptions
	const {
		data: vectorUploads,
		error: vectorUploadsError,
		isFetching: vectorUploadsIsFetching,
	} = useQuery(vectorDataLayerUploadsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	// For each vectorUploads, fetch preprocessed GeoJSON and it's metadata (same as result of last POST
	// upload/preprocessing request)
	const dataLayerUploadsGeoJsonQueries = useQueries({
		queries: vectorUploads
			? vectorUploads.map((v) => ({
					enabled: !!v.stdDataSasUrl,
					queryKey: ["FetchFromAzureFileShare", v.id],
					queryFn: async () => {
						const response = await fetchFromAzureFileShare(v.stdDataSasUrl!);
						const result = (await response.json()) as VectorDataLayerUploadStdResult;
						return result;
					},
				}))
			: [],
	});

	// Fetch data layer field associations
	const {
		data: dataLayerFieldAssociations,
		error: dataLayerFieldAssociationsError,
		isFetching: dataLayerFieldAssociationsIsFetching,
	} = useQuery(dataLayerFieldAssociationsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	// Create data layer field association - mutation
	const { mutateAsync: createDataLayerFieldAssociationMutationAsync } = useMutation({
		mutationFn: createDataLayerFieldAssociation,
	});

	// Delete data layer field association
	const { mutateAsync: deleteDataLayerFieldAssociationMutationAsync } = useMutation({
		mutationFn: deleteDataLayerFieldAssociation,
	});

	/*
	 * Effects
	 */

	// Setup initial state of field <> data layer associations.
	useEffect(() => {
		if (dataLayerFieldAssociations === undefined) {
			return;
		}
		const nextAssocFields = new Set(dataLayerFieldAssociations);
		setAssociatedFields(nextAssocFields);
		setExistingAssociatedFields(nextAssocFields);
	}, [dataLayerFieldAssociations]);

	// Setup initial state of Data Layer Display selection
	useEffect(() => {
		if (vectorUploads === undefined) {
			return;
		}
		if (vectorUploads.length === 0) {
			setSelectedDataLayerDisplayId(undefined);
			return;
		}
		setSelectedDataLayerDisplayId((prev) => {
			if (prev && vectorUploads.some((v) => v.id === prev)) {
				return prev;
			}
			return vectorUploads[0].id;
		});
	}, [vectorUploads]);

	// Handle dataLayerOptions query failure
	useEffect(() => {
		if (dataLayerOptionsError !== null) {
			console.error(dataLayerOptionsError);
			toast.error(dataLayerOptionsError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [dataLayerOptionsError]);

	// Handle vectorUploads query failure
	useEffect(() => {
		if (vectorUploadsError !== null) {
			console.error(vectorUploadsError);
			toast.error(vectorUploadsError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [vectorUploadsError]);

	// Handle search fields query failure
	useEffect(() => {
		if (searchFieldsByDataLayerError !== null) {
			console.error(searchFieldsByDataLayerError);
			toast.error(searchFieldsByDataLayerError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [searchFieldsByDataLayerError]);

	// Handle data layer field associations query failure
	useEffect(() => {
		if (dataLayerFieldAssociationsError !== null) {
			console.error(dataLayerFieldAssociationsError);
			toast.error(dataLayerFieldAssociationsError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [dataLayerFieldAssociationsError]);

	// Notify parent when field associations are complete (at least one persisted association)
	useEffect(() => {
		const complete = existingAssociatedFields.size > 0;
		if (complete !== fieldAssocComplete) {
			onFieldAssocComplete(complete);
		}
	}, [existingAssociatedFields, fieldAssocComplete, onFieldAssocComplete]);

	// Setup initial state of plotVisibility records
	useEffect(() => {
		if (fieldsWithBoundaries) {
			const initialState: Record<UUID, boolean> = {};
			fieldsWithBoundaries.forEach((field) => {
				initialState[field.fieldId] = false;
			});
			setPlotsVisibleOnMap(initialState);
		}
	}, [fieldsWithBoundaries]);

	/*
	 * Callbacks
	 */

	// Back button press
	const handleBackButton = () => {
		history.push(moduleNavigation.createFlightLink(true, null, "data-layer-upload"));
	};

	// Continue button press
	const handleContinue = () => {
		history.push(moduleNavigation.createFlightLink(true, dataLayerId, "data-layer-plot-results"));
	};

	// Data Layer Types / Zones DDL selection change
	const handleDataLayerDisplayChange = (vectorDataLayerUploadId: UUID) => {
		if (vectorUploads === undefined) {
			return;
		}
		if (vectorUploads.some(({ id }) => id === vectorDataLayerUploadId)) {
			setSelectedDataLayerDisplayId(vectorDataLayerUploadId);
		}
	};

	const handleVisiblePlotsChange = (fieldId: UUID, visible: boolean) => {
		setPlotsVisibleOnMap((prev) => ({
			...prev,
			[fieldId]: visible,
		}));
	};

	const handleAssociateChange = (fieldId: UUID, associated: boolean) => {
		if (!dataLayerId) {
			return;
		}
		try {
			setAssociatedFields((prev) => {
				const next = new Set(prev);
				if (associated) {
					next.add(fieldId);
				} else {
					next.delete(fieldId);
				}
				return next;
			});
		} catch (error) {
			console.error(error);
			const errorMsg = error instanceof Error ? error.message : "Failed to update data layer field association";
			toast.error(errorMsg);
			throw error;
		}
	};

	const handleSort = (column: SortColumn) => {
		setSortConfig((prev) => {
			if (prev.column === column) {
				return {
					column,
					direction: prev.direction === "ascending" ? "descending" : "ascending",
				};
			}
			return {
				column,
				direction: column === "growingSeason" ? "descending" : "ascending",
			};
		});
	};

	const persistAssociationChanges = async (): Promise<boolean> => {
		if (!dataLayerId || isSavingAssociations) {
			return false;
		}
		if (dataLayerFieldAssociations === undefined) {
			toast.error("Field associations are not loaded. Please wait and try again.");
			return false;
		}

		const nextAssociatedFields = new Set(associatedFields);
		const prevAssociatedFields = new Set(existingAssociatedFields);
		const fieldsToCreate = [...nextAssociatedFields].filter((fieldId) => !prevAssociatedFields.has(fieldId));
		const fieldsToDelete = [...prevAssociatedFields].filter((fieldId) => !nextAssociatedFields.has(fieldId));

		if (fieldsToCreate.length === 0 && fieldsToDelete.length === 0) {
			return true;
		}

		setIsSavingAssociations(true);
		try {
			for (const fieldId of fieldsToCreate) {
				await createDataLayerFieldAssociationMutationAsync({
					getAccessToken: getTokenSilently,
					clientId: userAuth.currentClientId,
					dataLayerId,
					fieldId,
				});
			}

			for (const fieldId of fieldsToDelete) {
				await deleteDataLayerFieldAssociationMutationAsync({
					getAccessToken: getTokenSilently,
					clientId: userAuth.currentClientId,
					dataLayerId,
					fieldId,
				});
			}

			queryClient.setQueryData(
				["GetDataLayerFieldAssociations", userAuth.currentClientId, dataLayerId],
				[...nextAssociatedFields],
			);
			setExistingAssociatedFields(nextAssociatedFields);
			toast.success("Field associations saved");
			return true;
		} catch (error) {
			console.error(error);
			const errorMsg = error instanceof Error ? error.message : "Failed to save data layer field associations";
			toast.error(errorMsg);
			return false;
		} finally {
			setIsSavingAssociations(false);
		}
	};

	const handleSave = async () => {
		await persistAssociationChanges();
	};

	const handleSaveAndContinue = async () => {
		const saved = await persistAssociationChanges();
		if (!saved) {
			return;
		}
		handleContinue();
	};

	/*
	 * Derived state and memoization.
	 */
	const selectedDataLayerDisplayIndex = useMemo(() => {
		if (!vectorUploads || !selectedDataLayerDisplayId) {
			return -1;
		}
		return _.findIndex(vectorUploads, ({ id }) => id === selectedDataLayerDisplayId);
	}, [vectorUploads, selectedDataLayerDisplayId]);

	const hasSelectedLayer = selectedDataLayerDisplayIndex >= 0;
	const isDirty = useMemo(() => {
		if (existingAssociatedFields.size !== associatedFields.size) {
			return true;
		}

		for (const fieldId of associatedFields) {
			if (!existingAssociatedFields.has(fieldId)) {
				return true;
			}
		}

		return false;
	}, [associatedFields, existingAssociatedFields]);

	const dataLayerVectorUpload = hasSelectedLayer
		? dataLayerUploadsGeoJsonQueries[selectedDataLayerDisplayIndex]?.data
		: undefined;

	const dataLayerNumZones = hasSelectedLayer
		? vectorUploads![selectedDataLayerDisplayIndex].vectorDataNumZones
		: undefined;

	const sortedFieldsWithBoundaries = useMemo(() => {
		if (!fieldsWithBoundaries) {
			return [];
		}

		const getColumnValue = (field: FieldWithBoundaryAndPlots) =>
			sortConfig.column === "field" ? field.fieldName : field.growingSeasonName;

		const sorted = [...fieldsWithBoundaries].sort((a, b) => {
			const comparison = getColumnValue(a).localeCompare(getColumnValue(b), undefined, {
				numeric: true,
				sensitivity: "base",
			});
			return sortConfig.direction === "ascending" ? comparison : -comparison;
		});

		return sorted;
	}, [fieldsWithBoundaries, sortConfig]);

	const selectedFieldsWithBoundaries = fieldsWithBoundaries
		? fieldsWithBoundaries.filter(({ fieldId }) => plotsVisibleOnMap[fieldId])
		: [];

	const selectedMapFieldBoundaries = selectedFieldsWithBoundaries.map(({ fieldBoundary }) => fieldBoundary);

	const selectedMapPlotBoundaries = selectedFieldsWithBoundaries.map(({ plotBoundaries }) => plotBoundaries);

	const selectedMapFieldIds = selectedFieldsWithBoundaries.map(({ fieldId }) => fieldId);

	const selectedMapFlightIds = selectedFieldsWithBoundaries.map(({ flightId }) => flightId);

	// Generate select menu options for the vector data layer uploads
	// Use the dataLayerTypeId and the number of zones to format labels
	const dataLayerSelectOptions = useMemo(() => {
		if (vectorUploads === undefined || dataLayerOptions === undefined) {
			return [];
		}
		const dataLayerTypeLabelById: Record<UUID, string> = Object.fromEntries(
			dataLayerOptions.dataLayerTypes.map((dlt) => {
				const id = dlt.id;
				const text = dlt.presetName || dlt.name;
				return [id, text];
			}),
		);
		const selectOpts: DataLayerTypeSelectOption[] = vectorUploads.map((v) => {
			const key = v.id;
			const value = v.id;
			const label = dataLayerTypeLabelById[v.dataLayerTypeId];
			const text = `${label} (${v.vectorDataNumZones} zone)`;
			return { key, value, text };
		});
		return _.sortBy(selectOpts, "text");
	}, [vectorUploads, dataLayerOptions]);

	const fieldLinks: Record<string, string> = useMemo(() => {
		if (fieldsWithBoundaries) {
			return fieldsWithBoundaries.reduce<Record<string, string>>((acc, field) => {
				acc[field.fieldId] = moduleNavigation.createFieldLink(false, field.fieldId);
				return acc;
			}, {});
		}
		return {};
	}, [fieldsWithBoundaries, moduleNavigation]);

	// early-out if component is fetching initial state or initial effects
	const loaderActive: boolean =
		dataLayerOptionsIsFetching ||
		vectorUploadsIsFetching ||
		searchFieldsByDataLayerIsFetching ||
		dataLayerFieldAssociationsIsFetching ||
		_.some(dataLayerUploadsGeoJsonQueries.map(({ isFetching: y }) => y)) ||
		((vectorUploads?.length ?? 0) > 0 && !hasSelectedLayer);

	const validationErrorMessage =
		fieldsWithBoundaries && fieldsWithBoundaries.length == 0
			? "No flight images were found for this data layer's footprint. Please check the extent and area of interest of the uploaded vector data."
			: null;

	/*
	 * Rendering
	 */

	if (loaderActive) {
		return (
			<div className={styles.loader}>
				<Loader active inline="centered" />
			</div>
		);
	}

	if (failedToLoad) {
		return (
			<div className={styles.segment}>
				<Segment basic>
					<Message negative>
						<MessageHeader>Error</MessageHeader>
						<p>Error while preparing Vector Data Field Associations. Please try again.</p>
					</Message>
				</Segment>
			</div>
		);
	}

	return (
		<Segment basic vertical className={styles.segment}>
			<Grid stackable className={styles.actionsRow} columns={2}>
				<Grid.Row verticalAlign="middle">
					<Grid.Column className={styles.actionsBackCol}>
						<Button secondary compact content="Back" onClick={handleBackButton} />
					</Grid.Column>
					<Grid.Column className={styles.actionsButtonsCol}>
						<div className={styles.actionsButtonGroup}>
							<Button
								color="blue"
								compact
								content="Save"
								disabled={!isDirty || isSavingAssociations}
								onClick={() => void handleSave()}
							/>
							{isDirty ? (
								<Button
									primary
									compact
									content="Save and Continue"
									disabled={associatedFields.size === 0 || isSavingAssociations}
									onClick={() => void handleSaveAndContinue()}
								/>
							) : (
								<Button
									primary
									compact
									content="Continue"
									disabled={existingAssociatedFields.size === 0}
									onClick={handleContinue}
								/>
							)}
						</div>
					</Grid.Column>
				</Grid.Row>
			</Grid>
			<Grid stackable columns={16} className={styles.assocContentGrid}>
				<Grid.Column computer={7} className={styles.infoSegment}>
					<div className={styles.sectionHeaderRow}>
						<Header as="h3" size="medium" className={styles.sectionHeader}>
							Associated Fields
						</Header>
					</div>
					{validationErrorMessage ? (
						<Segment basic>
							<Message error content={validationErrorMessage} icon="info circle" className={styles.validationMsg} />
						</Segment>
					) : (
						<div className={styles.tableScrollContainer}>
							<Table celled striped sortable selectable compact textAlign="left">
								<Table.Header>
									<Table.HeaderCell
										sorted={sortConfig.column === "field" ? sortConfig.direction : undefined}
										onClick={() => handleSort("field")}
									>
										Field
									</Table.HeaderCell>
									<Table.HeaderCell
										sorted={sortConfig.column === "growingSeason" ? sortConfig.direction : undefined}
										onClick={() => handleSort("growingSeason")}
									>
										Growing Season
									</Table.HeaderCell>
									<Table.HeaderCell className={styles.actionsCol} textAlign="center">
										Actions
									</Table.HeaderCell>
								</Table.Header>
								<Table.Body>
									{sortedFieldsWithBoundaries.map((field) => (
										<Table.Row key={field.fieldId}>
											<Table.Cell>
												<Link to={fieldLinks[field.fieldId]} target="_blank">
													{field.fieldName}
												</Link>
											</Table.Cell>
											<Table.Cell>{field.growingSeasonName}</Table.Cell>
											<Table.Cell className={styles.actionsCol} textAlign="center">
												<FieldActions
													fieldId={field.fieldId}
													isAssociated={associatedFields.has(field.fieldId)}
													existingAssociation={existingAssociatedFields.has(field.fieldId)}
													isVisible={!!plotsVisibleOnMap[field.fieldId]}
													onVisiblePlotsChange={handleVisiblePlotsChange}
													onAssociateChange={handleAssociateChange}
												/>
											</Table.Cell>
										</Table.Row>
									))}
								</Table.Body>
							</Table>
						</div>
					)}
				</Grid.Column>
				<Grid.Column computer={9} className={styles.mapSegment}>
					<div className={styles.sectionHeaderWithControl}>
						<Header as="h3" size="medium" className={styles.sectionHeader}>
							Data Layer Display
						</Header>
						<Form className={styles.displayDropdownForm}>
							<Form.Field>
								<Tooltip overlay="Select data layer upload / zonation to preview" placement="top">
									<Dropdown
										fluid
										compact
										selection
										search
										options={dataLayerSelectOptions}
										value={hasSelectedLayer ? vectorUploads![selectedDataLayerDisplayIndex].id : undefined}
										disabled={dataLayerSelectOptions.length === 0}
										onChange={(_, { value }) => handleDataLayerDisplayChange(value as UUID)}
									/>
								</Tooltip>
							</Form.Field>
						</Form>
					</div>
					<div className={styles.mapContainer}>
						{dataLayerNumZones && dataLayerVectorUpload && (
							<DataLayerMap
								dataLayerNumZones={dataLayerNumZones}
								fieldIds={selectedMapFieldIds}
								flightIds={selectedMapFlightIds}
								dataLayerFeatures={dataLayerVectorUpload}
								fieldBoundaries={selectedMapFieldBoundaries}
								plotBoundaries={selectedMapPlotBoundaries}
							/>
						)}
					</div>
				</Grid.Column>
			</Grid>
		</Segment>
	);
};

export default VectorDataLayerFieldAssoc;
